import React, { useState } from 'react';
import { 
  LayoutDashboard, 
  FileText, 
  Zap, 
  LogOut, 
  Menu, 
  Trophy,
  Activity
} from 'lucide-react';
import AnalysisView from './AnalysisView';
import SportsBetsView from './SportsBetsView';
import SportsAnalysisView from './SportsAnalysisView';
import AnimalitosView from './AnimalitosView';

export default function Dashboard({ onLogout }) {
  const [activeTab, setActiveTab] = useState('inicio');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Función para renderizar el contenido dinámico
  const renderContent = () => {
    switch (activeTab) {
      case 'inicio':
        return (
          <div className="max-w-4xl mx-auto text-center mt-20 animate-in fade-in zoom-in duration-700">
            <div className="bg-slate-900/50 border border-slate-800 p-12 rounded-[3rem] shadow-2xl relative">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-yellow-500 to-transparent opacity-50"></div>
              <Trophy size={60} className="text-yellow-500 mx-auto mb-6 drop-shadow-[0_0_15px_rgba(234,179,8,0.4)]" />
              <h1 className="text-4xl font-black mb-4 italic tracking-tight uppercase">Bienvenido a KronoLogic AI</h1>
              <p className="text-slate-400 text-lg max-w-lg mx-auto">
                Tu ecosistema inteligente para el análisis hípico y estadísticas de potencia en tiempo real.
              </p>
              
              <div className="mt-10 grid grid-cols-2 gap-4">
                <div className="bg-slate-950 p-6 rounded-2xl border border-slate-800">
                  <p className="text-yellow-500 font-black text-2xl">94.2%</p>
                  <p className="text-slate-500 text-[10px] uppercase font-bold tracking-widest">Efectividad IA</p>
                </div>
                <div className="bg-slate-950 p-6 rounded-2xl border border-slate-800">
                  <p className="text-blue-400 font-black text-2xl">REAL-TIME</p>
                  <p className="text-slate-500 text-[10px] uppercase font-bold tracking-widest">Servidor Activo</p>
                </div>
              </div>
            </div>
          </div>
        );
      
      case 'analisis':
        return <AnalysisView />;

      case 'deportes':
        return <SportsBetsView />;

      case 'analisis-deportes':
        return <SportsAnalysisView />;

      case 'animalitos':
        return <AnimalitosView />;

      default:
        return null;
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-950 text-white font-sans overflow-hidden">
      
      {/* SIDEBAR */}
      <aside 
        className={`${isMenuOpen ? 'w-64' : 'w-0 sm:w-20'} bg-slate-900 border-r border-slate-800 transition-all duration-300 flex flex-col overflow-hidden fixed sm:relative h-full z-50`}
      >
        <div className="p-6 flex items-center gap-3">
          <div className="bg-yellow-500 w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center shadow-[0_0_15px_rgba(234,179,8,0.3)]">
            <Zap size={18} className="text-slate-900 fill-current" />
          </div>
          {isMenuOpen && <span className="tracking-tighter uppercase font-black text-xl italic text-white">KronoLogic</span>}
        </div>

        <nav className="flex-1 mt-4 px-3 space-y-2">
          <NavItem 
            icon={<LayoutDashboard size={20} />} 
            label="Inicio" 
            active={activeTab === 'inicio'} 
            isOpen={isMenuOpen}
            onClick={() => setActiveTab('inicio')}
          />
          <NavItem 
            icon={<FileText size={20} />} 
            label="Análisis Hípico" 
            active={activeTab === 'analisis'} 
            isOpen={isMenuOpen}
            onClick={() => setActiveTab('analisis')}
          />
          <NavItem 
            icon={<Trophy size={20} />} 
            label="Análisis Logros Deportes" 
            active={activeTab === 'deportes'} 
            isOpen={isMenuOpen}
            onClick={() => setActiveTab('deportes')}
          />
          <NavItem 
            icon={<Activity size={20} />} 
            label="Análisis Deportes" 
            active={activeTab === 'analisis-deportes'} 
            isOpen={isMenuOpen}
            onClick={() => setActiveTab('analisis-deportes')}
          />
          <NavItem 
            icon={<Zap size={20} />} 
            label="Animalitos" 
            active={activeTab === 'animalitos'} 
            isOpen={isMenuOpen}
            onClick={() => setActiveTab('animalitos')}
          />
        </nav>

        <div className="p-4 border-t border-slate-800">
          <button 
            onClick={onLogout}
            className="w-full flex items-center gap-3 p-3 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-xl transition-all font-bold uppercase text-[10px] tracking-widest"
          >
            <LogOut size={18} />
            {isMenuOpen && <span>Cerrar Sesión</span>}
          </button>
        </div>
      </aside>

      {/* CONTENIDO PRINCIPAL */}
      <div className="flex-1 flex flex-col h-screen overflow-hidden ml-0 sm:ml-0">
        <header className="h-16 border-b border-slate-800 flex items-center justify-between px-4 sm:px-8 bg-slate-950/50 backdrop-blur-md">
          <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-slate-400 hover:text-white transition-colors p-2">
            <Menu size={24} />
          </button>
          
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-[10px] font-black text-yellow-500 uppercase tracking-widest">Estado del Sistema</p>
              <p className="text-xs font-bold text-green-500 flex items-center justify-end gap-1">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-ping"></span> ONLINE
              </p>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 sm:p-8 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-slate-900/40 via-transparent to-transparent">
          {renderContent()}
        </main>
      </div>
    </div>
  );
}

// Subcomponente para los items del menú para mantener el código limpio
function NavItem({ icon, label, active, isOpen, onClick, badge }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 p-3.5 rounded-xl transition-all duration-200 group relative ${
        active 
          ? 'bg-yellow-500 text-slate-900 shadow-[0_10px_20px_rgba(234,179,8,0.2)]' 
          : 'hover:bg-slate-800 text-slate-400 hover:text-white'
      }`}
    >
      <span className={`${active ? 'text-slate-900' : 'group-hover:text-yellow-500'}`}>{icon}</span>
      {isOpen && (
        <div className="flex justify-between items-center w-full">
          <span className="font-bold uppercase text-[11px] tracking-[0.1em]">{label}</span>
          {badge && (
            <span className={`text-[8px] px-1.5 py-0.5 rounded font-black border ${
              active ? 'bg-slate-900 text-yellow-500 border-slate-900' : 'bg-slate-950 text-yellow-500 border-yellow-500/30'
            }`}>
              {badge}
            </span>
          )}
        </div>
      )}
    </button>
  );
}